import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import {
  Button,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ListItemText,
  MenuItem,
  Switch,
  Typography,
  Box,
} from "@mui/material";
import { Inline } from "components/layout/reusableLayouts";
import EmployeeSearchAutoComplete, {
  Employee,
} from "../../common/EmployeeSearchAutoComplete";
import { UserIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_ACCOUNT } from "../../../graphql/account";
import { ADD_EXTERNAL_CARDS } from "../../../graphql/card";
import { toast } from "react-toastify";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { fullName } from "utils/fullName";

// Heroicons ship no width/height, so the size has to come from the call site.
const ICON_STYLE = {
  width: 20,
  height: 20,
  color: "var(--color-text-brand)",
};

interface MapUserExternalCardProps {
  accountId: string | null;
  cards: Card[];
  open: boolean;
  isEdit: boolean;
  onSuccess: () => void;
  onExit: () => void;
}

export interface Card {
  loadCard: boolean;
  cardIssuerId: string;
  nickname: string;
  cardNumber: string;
  ownerName: string;
  ownerEmail: string;
  user: User;
  possibleCards: [PossibleCard] | null;
  selectedCard: string;
}

interface PossibleCard {
  id: string;
  nickname: string;
  cardNumber: string;
  status: string;
  user: User;
}

interface User {
  id: string;
  firstName: string;
  lastName?: string | null;
}

interface HeadCell {
  id: keyof Card;
  label: string;
  sort?: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "loadCard",
    label: "Select",
  },
  {
    id: "cardNumber",
    label: "Card",
  },
  {
    id: "possibleCards",
    label: "Existing Cards",
  },
  {
    id: "user",
    label: "User",
  },
];

const EnhancedTableHead: React.FC = () => {
  const { t } = useTranslation();

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id}>
            <Inline
              justify="between"
              wrap={false}
              gap="0"
              sx={{ flexShrink: 0 }}
            >
              <Typography variant="subtitle2">{t(headCell.label)}</Typography>
            </Inline>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const MapUserExternalCardDrawer: React.FC<MapUserExternalCardProps> = (
  props,
) => {
  const { accountId, cards, open, isEdit, onSuccess, onExit } = props;
  const { t } = useTranslation();
  const [mappedCards, setMappedCards] = React.useState<Card[]>([]);
  const [syncStartDate, setSyncStartDate] = React.useState<number | null>(
    dayjs().startOf("day").valueOf(),
  );
  const [account, setAccount] = React.useState<any>(null);

  const [getAccount, { loading: accountLoading }] = useLazyQuery(GET_ACCOUNT, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setAccount(data.account);
    },
    onError: (error) => {
      toast.error(error.message || t("Error getting account"));
    },
  });
  const [addExternalCards, { loading }] = useMutation(ADD_EXTERNAL_CARDS);

  const handleSaveCards = () => {
    if (mappedCards.length === 0) {
      onExit();
      return;
    }
    const isMapped = mappedCards
      .filter((card) => card.loadCard)
      .every((card) => card?.user?.id || card.selectedCard);
    if (!isMapped) {
      toast.error(
        t("Please validate that you have assigned a user to each card"),
      );
      return;
    }
    if (!syncStartDate) {
      toast.error(t("Missing start date for syncing cards"));
      return;
    }
    if (dayjs().diff(syncStartDate, "days") > 30) {
      toast.error(t("Start date must be within the last 30 days"));
      return;
    }

    addExternalCards({
      variables: {
        accountId: accountId,
        startDate: syncStartDate,
        input: mappedCards
          .map((card) => {
            if (!card?.loadCard) return;
            return {
              cardIssuerId: card.cardIssuerId,
              selectedCardId: card.selectedCard,
              userId: card.user.id,
            };
          })
          .filter((card) => card),
      },
      onCompleted: (data) => {
        toast.success(t("External cards saved successfully"));
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message || t("Error saving external cards"));
      },
    });
  };

  const handleSelectCard = (cardId: string, idx: number) => {
    if (!cardId) return;

    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      const possibleCard = updatedCards[idx].possibleCards?.find(
        (card) => card.id === cardId,
      );
      updatedCards[idx] = {
        ...updatedCards[idx],
        user: {
          id: possibleCard?.user?.id || "",
          firstName: possibleCard?.user?.firstName || "",
          lastName: possibleCard?.user?.lastName || "",
        },
        selectedCard: cardId,
      };
      return updatedCards;
    });
  };

  const handleSelectEmployee = (employee: Employee | null, idx: number) => {
    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      updatedCards[idx] = {
        ...updatedCards[idx],
        user: {
          id: employee?.id || "",
          firstName: employee?.firstName || "",
          lastName: employee?.lastName || "",
        },
        selectedCard: "",
      };
      return updatedCards;
    });
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    setSyncStartDate(date.startOf("day").valueOf());
  };

  const handleLoadCard = (loadCard: boolean, idx: number) => {
    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      updatedCards[idx] = {
        ...updatedCards[idx],
        loadCard: loadCard,
      };
      return updatedCards;
    });
  };

  useEffect(() => {
    setMappedCards(cards.map((card) => ({ ...card, loadCard: true })));
  }, [cards]);

  useEffect(() => {
    if (accountId) {
      getAccount({
        variables: {
          id: accountId,
        },
      });
    }
  }, [accountId]);

  return (
    <Drawer
      open={open}
      onClose={onExit}
      title={t("Map External Cards")}
      loading={loading || accountLoading}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={() => {
              setMappedCards([]);
              onExit();
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={async () => {
              handleSaveCards();
            }}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <>
        {account && !account?.startAt && (
          <>
            <Inline
              wrap={false}
              gap="var(--space-3)"
              sx={{ marginBottom: "var(--space-5)" }}
            >
              <CreditCardIcon style={ICON_STYLE} />
              <Typography variant="h3">{t("Start Date")}</Typography>
            </Inline>
            <Typography variant="body2" sx={{ marginBottom: "var(--space-3)" }}>
              {t("transactionSyncDateNotice")}
            </Typography>
            <Box sx={{ marginBottom: "var(--space-11)" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(syncStartDate)}
                  onChange={(date) => handleDateChange(date)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </>
        )}
        <Inline
          wrap={false}
          gap="var(--space-3)"
          sx={{ marginBottom: "var(--space-5)" }}
        >
          <UserIcon style={ICON_STYLE} />
          <Typography variant="h3">
            {t("External Card User Mapping")}
          </Typography>
        </Inline>
        <Typography variant="body2">
          {t("Review each card to ensure it is mapped to the correct user")}
        </Typography>

        <TableContainer sx={{ width: "100%" }}>
          <Table stickyHeader sx={{ paddingX: "10px" }}>
            <EnhancedTableHead />
            <TableBody>
              {accountId && mappedCards?.length > 0 ? (
                mappedCards.map((data: any, idx: number) => (
                  <TableRow sx={{ height: "50px" }} key={idx}>
                    <TableCell>
                      <Switch
                        checked={data.loadCard}
                        onChange={(event) => {
                          handleLoadCard(event.target.checked, idx);
                        }}
                        sx={{ marginRight: "var(--space-5)" }}
                        slotProps={{
                          input: { "aria-label": "controlled" },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: "192px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <ListItemText
                        primary={data.cardNumber}
                        secondary={
                          data.ownerName ||
                          "" + (data.nickname ? " | " + data.nickname : "")
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: "256px", minWidth: "192px" }}>
                      {data.possibleCards ? (
                        <Select
                          value={data.selectedCard || "New"}
                          onChange={(e: any) =>
                            handleSelectCard(e.target.value, idx)
                          }
                          fullWidth
                          size="small"
                          inputProps={{ "aria-label": "Without label" }}
                          renderValue={(selected) => {
                            if (selected === "New") return "New Card";
                            const selectedCard = data.possibleCards?.find(
                              (card: PossibleCard) => card.id === selected,
                            );
                            return (
                              selectedCard?.nickname +
                              " | " +
                              selectedCard?.cardNumber
                            );
                          }}
                        >
                          <MenuItem value="New">
                            <ListItemText primary={t("New Card")} />
                          </MenuItem>
                          {data.possibleCards?.map((card: PossibleCard) => (
                            <MenuItem key={card.id} value={card.id}>
                              <ListItemText
                                primary={
                                  card.nickname + " | " + card.cardNumber
                                }
                                secondary={fullName(card?.user)}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body1">{t("New Card")}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: "192px" }}>
                      <EmployeeSearchAutoComplete
                        title="Matched User"
                        handleSelectEmployee={(employee) =>
                          handleSelectEmployee(employee, idx)
                        }
                        employee={data.user}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length}>
                    {!account?.startAt ? (
                      <Inline
                        wrap={false}
                        component="span"
                        justify="center"
                        gap="0"
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        <Typography component="span" variant="body1">
                          {t("ExternalCardProcessing")}
                        </Typography>
                      </Inline>
                    ) : (
                      <Inline
                        wrap={false}
                        component="span"
                        justify="center"
                        gap="0"
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        <Typography component="span" variant="body1">
                          {t("No new cards found")}
                        </Typography>
                      </Inline>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    </Drawer>
  );
};

export default MapUserExternalCardDrawer;
